/**
 * @fileoverview `<EmptyState>` — état vide réutilisable GNPC.
 *
 * Un état vide est souvent légitime : aucune contravention, aucune anomalie,
 * aucun remboursement en attente. Le composant impose un ton calme, sans
 * vocabulaire d'échec, et laisse les pages fournir une action explicite.
 */

import type { ReactNode } from 'react';
import { Inbox } from 'lucide-react';
import { cn } from '../lib/utils.ts';

export type EmptyStateTone = 'neutral' | 'positive' | 'info' | 'warning' | 'danger';

export interface EmptyStateProps {
  /** Icône visuelle courte. Par défaut : `Inbox`. */
  icon?: ReactNode;
  /** Titre métier de l'état vide. */
  title: ReactNode;
  /** Description courte, actionnable si possible. */
  description?: ReactNode;
  /** Action principale ou secondaire. */
  action?: ReactNode;
  /** Variante sémantique. */
  tone?: EmptyStateTone;
  /** Taille verticale du bloc. */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Élément de titre rendu. Par défaut `p` : un état vide en table ou en
   * carte ne doit pas introduire de titre dans la hiérarchie du document.
   * Passer `h1` quand l'état vide EST le contenu principal de la page (ex.
   * page 404), afin que la page expose bien un `<h1>` (règle axe
   * `page-has-heading-one`).
   */
  titleAs?: 'p' | 'h1' | 'h2' | 'h3';
  /** Classe CSS complémentaire. */
  className?: string;
}

const TONE_CLASSES: Record<EmptyStateTone, string> = {
  neutral: 'text-muted-foreground bg-muted',
  positive: 'text-status-paid bg-status-paid/10',
  info: 'text-quipux-blue-700 bg-quipux-blue-50',
  warning: 'text-warning bg-warning/10',
  danger: 'text-danger bg-danger/10',
};

const SIZE_CLASSES = {
  sm: 'py-6',
  md: 'py-10',
  lg: 'py-14',
} as const;

/**
 * État vide standard.
 *
 * @param props - Icône, titre, description, action, ton, taille et classe CSS.
 * @returns Bloc centré compatible tables, cards et pages.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   tone="positive"
 *   title="Aucune anomalie"
 *   description="Tous les paiements encaissés sont enregistrés."
 * />
 * ```
 */
export function EmptyState({
  icon = <Inbox />,
  title,
  description,
  action,
  tone = 'neutral',
  size = 'md',
  titleAs: TitleTag = 'p',
  className,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div className={cn('flex w-full flex-col items-center gap-3 px-4 text-center', SIZE_CLASSES[size], className)}>
      <div className={cn('flex size-12 items-center justify-center rounded-full [&>svg]:size-6', TONE_CLASSES[tone])}>
        {icon}
      </div>
      <div className="max-w-md space-y-1">
        <TitleTag className="text-quipux-navy-950 text-base font-semibold">{title}</TitleTag>
        {description ? <p className="text-muted-foreground text-sm leading-6">{description}</p> : null}
      </div>
      {action ? <div className="mt-1 flex flex-wrap justify-center gap-2">{action}</div> : null}
    </div>
  );
}
